import React, { useState, useEffect } from 'react';
import { Star, TrendingUp, TrendingDown, Minus, Users, ChevronRight } from 'lucide-react';
import { csatAPI } from '../utils/api';

const CSAT_LEVELS = {
  excellent: { min: 96, label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' },
  healthy: { min: 86, max: 95, label: 'Healthy', color: 'text-teal-600', bg: 'bg-teal-100', border: 'border-teal-200' },
  average: { min: 76, max: 85, label: 'Average', color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200' },
  poor: { max: 75, label: 'Poor', color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' }
};

const getCSATLevel = (score) => {
  if (score === null || score === undefined) return null;
  if (score > 95) return CSAT_LEVELS.excellent;
  if (score >= 86) return CSAT_LEVELS.healthy;
  if (score >= 76) return CSAT_LEVELS.average;
  return CSAT_LEVELS.poor;
};

const DATE_FILTERS = [
  { label: '15 Days', days: 15 },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: '180 Days', days: 180 },
  { label: '365 Days', days: 365 },
];

export function OfficerCSATCard({ showLegend = true }) {
  const [csatData, setCSATData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    fetchCSAT();
  }, [selectedDays]);

  const fetchCSAT = async () => {
    setLoading(true);
    try {
      const res = await csatAPI.getMyCSAT(selectedDays);
      setCSATData(res.data);
    } catch (err) {
      console.log('Could not fetch CSAT');
      setCSATData(null);
    } finally {
      setLoading(false);
    }
  };

  const level = csatData?.average_csat ? getCSATLevel(csatData.average_csat) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="officer-csat-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Star className="text-amber-500" size={20} />
          My CSAT Score
        </h3>
        <select
          value={selectedDays}
          onChange={(e) => setSelectedDays(Number(e.target.value))}
          className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg"
          data-testid="csat-date-filter"
        >
          {DATE_FILTERS.map((f) => (
            <option key={f.days} value={f.days}>{f.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="animate-pulse h-24 bg-slate-100 rounded-lg"></div>
      ) : csatData?.average_csat ? (
        <>
          <div className={`p-4 rounded-lg ${level?.bg} ${level?.border} border mb-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-slate-900">{csatData.average_csat}%</p>
                <p className={`text-sm font-medium ${level?.color} mt-1`}>{level?.label}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Based on</p>
                <p className="text-lg font-semibold text-slate-900">{csatData.total_ratings} ratings</p>
              </div>
            </div>
          </div>

          {/* Rating Breakdown */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="text-center p-2 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">{csatData.rating_breakdown?.excellent || 0}</p>
              <p className="text-xs text-slate-500">Excellent</p>
            </div>
            <div className="text-center p-2 bg-teal-50 rounded-lg">
              <p className="text-lg font-bold text-teal-600">{csatData.rating_breakdown?.healthy || 0}</p>
              <p className="text-xs text-slate-500">Healthy</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg">
              <p className="text-lg font-bold text-amber-600">{csatData.rating_breakdown?.average || 0}</p>
              <p className="text-xs text-slate-500">Average</p>
            </div>
            <div className="text-center p-2 bg-red-50 rounded-lg">
              <p className="text-lg font-bold text-red-600">{csatData.rating_breakdown?.poor || 0}</p>
              <p className="text-xs text-slate-500">Poor</p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-6">
          <Star size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No CSAT ratings yet</p>
          <p className="text-sm text-slate-400 mt-1">Ratings will appear after clients complete surveys</p>
        </div>
      )}

      {/* Legend */}
      {showLegend && (
        <div className="border-t border-slate-200 pt-4 mt-4">
          <p className="text-xs font-medium text-slate-500 mb-2">CSAT Score Guide:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-slate-600">Below 75%: <span className="font-medium text-red-600">Poor</span></span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-slate-600">76-85%: <span className="font-medium text-amber-600">Average</span></span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
              <span className="text-slate-600">86-95%: <span className="font-medium text-teal-600">Healthy</span></span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-slate-600">Above 95%: <span className="font-medium text-green-600">Excellent</span></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CompanyCSATCard({ onOfficerClick }) {
  const [csatData, setCSATData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [showOfficers, setShowOfficers] = useState(false);

  useEffect(() => {
    fetchCSAT();
  }, [selectedDays]);

  const fetchCSAT = async () => {
    setLoading(true);
    try {
      const res = await csatAPI.getCompanyCSAT(selectedDays);
      setCSATData(res.data);
    } catch (err) {
      console.log('Could not fetch company CSAT');
      setCSATData(null);
    } finally {
      setLoading(false);
    }
  };

  const level = csatData?.average_csat ? getCSATLevel(csatData.average_csat) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="company-csat-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Star className="text-amber-500" size={20} />
          Company CSAT
        </h3>
        <select
          value={selectedDays}
          onChange={(e) => setSelectedDays(Number(e.target.value))}
          className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg"
          data-testid="company-csat-date-filter"
        >
          {DATE_FILTERS.map((f) => (
            <option key={f.days} value={f.days}>{f.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="animate-pulse h-20 bg-slate-100 rounded-lg"></div>
      ) : csatData?.average_csat ? (
        <>
          <div 
            className={`p-4 rounded-lg ${level?.bg} ${level?.border} border cursor-pointer hover:shadow-md transition-all`}
            onClick={() => setShowOfficers(!showOfficers)}
            data-testid="company-csat-summary"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-slate-900">{csatData.average_csat}%</p>
                <p className={`text-sm font-medium ${level?.color} mt-1`}>{level?.label}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">{csatData.total_ratings} ratings</p>
                <p className="text-xs text-slate-400 mt-1">Click to view officers</p>
              </div>
              <ChevronRight size={24} className={`text-slate-400 transition-transform ${showOfficers ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {/* Officers List */}
          {showOfficers && csatData.officers && csatData.officers.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="officers-csat-list">
              <p className="text-sm font-medium text-slate-700 mb-2">Officer Performance</p>
              {csatData.officers.map((officer) => {
                const officerLevel = getCSATLevel(officer.average_csat);
                return (
                  <div 
                    key={officer.officer_id}
                    className={`p-3 rounded-lg border ${officerLevel?.bg} ${officerLevel?.border} flex items-center justify-between cursor-pointer hover:shadow-sm transition-all`}
                    onClick={() => onOfficerClick && onOfficerClick(officer.officer_id)}
                    data-testid={`officer-csat-${officer.officer_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border">
                        <Users size={18} className="text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{officer.officer_name}</p>
                        <p className="text-xs text-slate-500">{officer.total_ratings} ratings</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${officerLevel?.color}`}>{officer.average_csat}%</p>
                      <p className={`text-xs ${officerLevel?.color}`}>{officerLevel?.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-6">
          <Star size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No CSAT ratings yet</p>
        </div>
      )}
    </div>
  );
}

export function CSATStatCard({ score, label, onClick }) {
  const level = getCSATLevel(score);
  
  return (
    <div 
      className={`stat-card cursor-pointer hover:shadow-md transition-all ${level ? level.bg : 'bg-slate-50'} ${level ? level.border : 'border-slate-200'} border`}
      onClick={onClick}
      data-testid="csat-stat-card"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600 mb-1">{label}</p>
          <p className={`text-3xl font-bold ${level ? level.color : 'text-slate-400'}`}>
            {score !== null && score !== undefined ? `${score}%` : '--'}
          </p>
          {level && (
            <p className={`text-xs font-medium ${level.color} mt-1`}>{level.label}</p>
          )}
        </div>
        <div className={`${level ? level.bg : 'bg-amber-100'} p-3 rounded-lg`}>
          <Star className={level ? level.color : 'text-amber-500'} size={24} />
        </div>
      </div>
    </div>
  );
}
